import { Store } from '@ngxs/store';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { Animations } from 'src/app/core/angular-animations/angular-animations';
import { MenuItemModel } from 'src/app/core/models/menu-id.model';
import {
  LoadSpecificMenuStructure,
  OpenMenuItem,
  CloseMenuItem,
} from 'src/app/core/state/menu-tree-structure.actions';
import { ModuleNumbers } from 'src/app/core/enums/module-numbers.enum';
import { DndDragImageOffsetFunction, DndDropzoneDirective } from 'ngx-drag-drop';
import { Observable, Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ResetCurrentFilter, ApplyAdditional } from 'src/app/core/state/main-table-filter.actions';
import { MenuTreeStructureState } from 'src/app/core/state/menu-tree-structure.state';
import { MainTableFilterModulesEnum } from 'src/app/core/enums/main-table-filter-modules.enum';
import { MenuListLogicService } from 'src/app/core/services/menu-list-logic.service';
import { MenuDragDropService } from '../menu-drag-drop.service';

@Component({
  selector: 'app-menu-tree-single',
  templateUrl: './menu-tree-single.component.html',
  styleUrls: ['./menu-tree-single.component.scss'],
  animations: [Animations.toggleContainer, Animations.rotatedState],
  standalone: false,
})
export class MenuTreeSingleComponent implements OnInit, OnChanges, OnDestroy {
  getCurrentModuleNumber$: Observable<number> = this.store.select(
    MenuTreeStructureState.getCurrentModuleNumber
  );

  @Input() item: MenuItemModel;
  @Input() onlyAdd = false;
  @Input() showFunctionalButtons = true;
  @Input() menuItemsAsRouting = false;
  @Input() mainModules = false;
  @Input() tenantId: string;
  @Input() module: number;
  @Input() nextItem?: MenuItemModel;
  @Input() isLast = false;
  @Input() moduleNumber: ModuleNumbers;
  @Input() chosenElementId = '';
  @Input() isTopItem = false;
  @ViewChildren(DndDropzoneDirective) dropZones: QueryList<DndDropzoneDirective>;
  @ViewChild('tdzEl', { read: DndDropzoneDirective }) tdz: DndDropzoneDirective;
  @ViewChild('bdzEl', { read: DndDropzoneDirective }) bdz: DndDropzoneDirective;
  @ViewChild('dzEl', { read: DndDropzoneDirective }) dz: DndDropzoneDirective;

  @Output() readonly removeParent: EventEmitter<string> = new EventEmitter<string>();
  @Output() readonly chosenElementClicked: EventEmitter<string> = new EventEmitter<string>();

  hasChildren = false;
  showChildren = false;
  isChosen = false;

  // eslint-disable-next-line no-magic-numbers
  truncateThreshold = 75;
  private subscription = new Subscription();
  private currentModuleNumber: number;
  private openedMenuItemIds: string[] = [];
  private isInAdmin: boolean;

  constructor(
    private store: Store,
    public menuDNDService: MenuDragDropService,
    private menuListLogicService: MenuListLogicService
  ) {
    this.subscription.add(
      this.menuDNDService.dragStopped.subscribe(() => {
        this.resetDropzones();
      })
    );
  }

  ngOnInit(): void {
    this.isInAdmin = this.menuListLogicService.isCurrentUrlInAdmin();
    this.isElementChosen();
    this.subscription.add(
      this.getCurrentModuleNumber$.subscribe((moduleNumber: number) => {
        this.currentModuleNumber = moduleNumber;
      })
    );
    if (this.isInAdmin) {
      const module = this.module ?? this.currentModuleNumber;
      this.subscription.add(
        this.store
          .select(MenuTreeStructureState.getOpenedMenuItemIdsForModule(module))
          .subscribe((ids) => {
            this.openedMenuItemIds = ids;
            this.showChildren = this.openedMenuItemIds.includes(this.item.id);
          })
      );
    }
  }

  ngOnChanges(): void {
    this.hasChildren = this.checkIfRowHasChildren();
    this.isElementChosen();
    if (this.isInAdmin) {
      this.showChildren = this.openedMenuItemIds.includes(this.item.id);
    }
  }

  ngOnDestroy(): void {
    this.chosenElementClicked.emit('');
    this.subscription.unsubscribe();
  }

  toggleDropdown(): void {
    this.showChildren = !this.showChildren;
    if (this.isInAdmin) {
      const module = this.module ?? this.currentModuleNumber;
      const action = this.showChildren
        ? new OpenMenuItem(module, this.item.id)
        : new CloseMenuItem(module, this.item.id);
      this.store.dispatch(action);
    }
  }

  routeTo(): void {
    if (this.menuItemsAsRouting) {
      if (this.module === undefined) {
        this.module = this.currentModuleNumber;
      }

      // First dispatch the structure and filter reset
      this.store
        .dispatch([
          new ResetCurrentFilter(),
          new LoadSpecificMenuStructure(this.module, this.item.name, this.item.id),
        ])
        .pipe(
          switchMap(() => timer(100)), // Small delay to ensure menu structure is updated
          switchMap(() => {
            // Only apply document-specific filter if we're in the Documents module
            if (this.module === ModuleNumbers.Document) {
              return this.store.dispatch(
                new ApplyAdditional(MainTableFilterModulesEnum.DocumentsByMenu, {
                  menuId: this.item.id,
                  tabName: 'published',
                })
              );
            }
            // For other modules, return empty observable
            return timer(0);
          })
        )
        .subscribe();
    }
    this.chosenElementClicked.emit(this.item.id);
  }

  reactOnShowChildrenChange(showChildren: boolean): void {
    this.showChildren = showChildren;
    this.hasChildren = this.checkIfRowHasChildren();
  }

  emitElementClicked(itemId: string): void {
    this.chosenElementClicked.emit(itemId);
  }

  trackById(index: number, item: MenuItemModel) {
    return item.id;
  }

  dragItemOffsetRight: DndDragImageOffsetFunction = (event: DragEvent, dragItem: Element) => {
    const itemStyle = window.getComputedStyle(dragItem);
    const paddingTop = parseFloat(itemStyle.paddingTop) || 0;
    const paddingLeft = parseFloat(itemStyle.paddingLeft) || 0;
    const borderTop = parseFloat(itemStyle.borderTopWidth) || 0;
    const borderLeft = parseFloat(itemStyle.borderLeftWidth) || 0;

    const x = dragItem.clientWidth - (event.offsetX + paddingLeft + borderLeft);
    return {
      x,
      y: event.offsetY + paddingTop + borderTop,
    };
  };

  onDragStart(event: DragEvent, item: MenuItemModel): void {
    this.menuDNDService.activeItem = item;
  }

  onDragEnd(): void {
    this.resetDropzones();
    this.menuDNDService.activeItem = null;
  }

  onDrop(placement: 'inside' | 'bottom' | 'top'): void {
    this.menuDNDService.onDrop(this.item, placement);
  }

  resetDropzone(dropZone: DndDropzoneDirective): void {
    dropZone?.onDragLeave(new DragEvent('dragleave'));
  }

  resetDropzones(): void {
    this.dropZones.forEach((dropZone) => {
      this.resetDropzone(dropZone);
    });
  }

  resetOtherDropzones(dropZoneToReset: DndDropzoneDirective): void {
    this.dropZones.forEach((dropZone) => {
      if (dropZoneToReset !== dropZone) dropZone.onDragLeave(new DragEvent('dragleave'));
    });
  }

  private isElementChosen(): void {
    this.isChosen = this.isElementChosenRecursively(this.item);
  }

  private checkIfRowHasChildren(): boolean {
    return this.item.children && this.item.children.length > 0;
  }

  // recursively check if an item or any of its children is chosen
  private isElementChosenRecursively(item: MenuItemModel): boolean {
    if (item.id === this.chosenElementId) return true;
    if (item.children && item.children.length > 0) {
      return item.children.some((child) => this.isElementChosenRecursively(child));
    }
    return false;
  }
}
